import { formatDisplayDate } from 'i18n/calendarLabels';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { PwaChip, PwaSheet } from 'pwa/components/ui';
import { DataJournal } from 'store/vat/models/vatReportData';

type DragCloseHandlers = Pick<
  React.HTMLAttributes<HTMLElement>,
  'onPointerDown' | 'onPointerMove' | 'onPointerUp' | 'onPointerCancel'
> & { style?: React.CSSProperties };

export type PwaVatTxnDetailSheetProps = {
  record: DataJournal;
  onClose: () => void;
  formatAmount: (value?: number) => string;
  formatText: (value?: string | number | null) => string;
  formatDate?: (value: string) => string;
  dragZoneRef?: React.RefObject<HTMLDivElement | null>;
  dragCloseHandlers?: DragCloseHandlers;
  /** Optional primary footer action (e.g. Change mapping). */
  footer?: React.ReactNode;
};

function KvRows({
  items,
}: {
  items: Array<[string, React.ReactNode]>;
}) {
  return (
    <dl className="pwa-kvrows pwa-list">
      {items.map(([label, value]) => (
        <div key={label}>
          <dt>{label}</dt>
          <dd>{value}</dd>
        </div>
      ))}
    </dl>
  );
}

function AmountCell({
  value,
  formatAmount,
}: {
  value?: number;
  formatAmount: (value?: number) => string;
}) {
  const n = value || 0;
  return (
    <b className={n < 0 ? 'pwa-neg' : undefined}>{formatAmount(n)}</b>
  );
}

export const PwaVatTxnDetailSheet: React.FC<PwaVatTxnDetailSheetProps> = ({
  record,
  onClose,
  formatAmount,
  formatText,
  formatDate,
  dragZoneRef,
  dragCloseHandlers,
  footer,
}) => {
  const { t, i18n } = useTranslation();

  const contact =
    String(record.contact ?? '').trim() ||
    String(record.description ?? '').trim() ||
    t('organisation.transactions');
  const invoice = String(record.invoiceNumber ?? '').trim();
  const action = String(
    record.exceptionActionType || record.selectedValue || ''
  ).trim();
  const sub = [invoice || null, action || null].filter(Boolean).join(' · ');

  const dateFmt = (raw?: string | null) => {
    if (!raw) return '—';
    if (formatDate) {
      const formatted = formatDate(String(raw));
      if (
        !formatted ||
        formatted === '-' ||
        /invalid/i.test(String(formatted))
      ) {
        return '—';
      }
      return formatted;
    }
    const formatted = formatDisplayDate(
      String(raw),
      'D MMM YYYY',
      i18n.language
    );
    return !formatted || /invalid/i.test(String(formatted)) ? '—' : formatted;
  };

  const vatTreatment: Array<[string, React.ReactNode]> = [
    [
      t('transactions.mapping'),
      action ? <PwaChip key="m">{action}</PwaChip> : formatText(null),
    ],
    [t('common.entity'), formatText(record.entityName)],
    [t('organisation.tax-name'), formatText(record.taxName)],
    [t('exceptions.sourceType'), formatText(record.sourceType)],
  ];

  const documentRows: Array<[string, React.ReactNode]> = [
    [t('transactions.contact'), formatText(record.contact)],
    [t('transactions.invoice-no'), formatText(record.invoiceNumber)],
    [t('common.description'), formatText(record.description)],
    [t('organisation.reference'), formatText(record.reference)],
    [
      t('vat-report.created-date'),
      dateFmt(record.journalCreatedDate as string),
    ],
    [t('vat-report.journal-date'), dateFmt(record.journalDate as string)],
  ];

  const accountRows: Array<[string, React.ReactNode]> = [
    [
      t('organisation.account-name'),
      [record.accountCode, record.accountName]
        .map((v) => String(v ?? '').trim())
        .filter(Boolean)
        .join(' · ') || '—',
    ],
    [t('transactions.accountType'), formatText(record.accountType)],
    [t('common.currency'), formatText(record.currency)],
  ];

  return (
    <PwaSheet
      full
      title={contact}
      sub={sub || undefined}
      onClose={onClose}
      dragZoneRef={dragZoneRef}
      dragCloseHandlers={dragCloseHandlers}
      footer={footer}
      className="scope-pwa-vat-txn-detail"
    >
      <div className="pwa-card scope-pwa-vat-txn-detail__totals">
        <div className="pwa-totals" style={{ borderTop: 0 }}>
          <div>
            <span>{t('transactions.net')}</span>
            <AmountCell
              value={record.netAmountConverted}
              formatAmount={formatAmount}
            />
          </div>
          <div>
            <span>{t('transactions.tax')}</span>
            <AmountCell
              value={record.taxAmountConverted}
              formatAmount={formatAmount}
            />
          </div>
          <div>
            <span>{t('transactions.gross')}</span>
            <AmountCell
              value={record.grossAmountConverted}
              formatAmount={formatAmount}
            />
          </div>
        </div>
      </div>

      <div className="pwa-section-head">
        <span>
          {t('vat-report.vat-treatment', { defaultValue: 'VAT treatment' })}
        </span>
      </div>
      <KvRows items={vatTreatment} />

      <div className="pwa-section-head">
        <span>{t('common.document', { defaultValue: 'Document' })}</span>
      </div>
      <KvRows items={documentRows} />

      <div className="pwa-section-head">
        <span>{t('organisation.account', { defaultValue: 'Account' })}</span>
      </div>
      <KvRows items={accountRows} />
    </PwaSheet>
  );
};
